// Editor de template WhatsApp — cria e submete à Meta.
//
// Espelha as regras da Graph (ver `model.ts` e, no back, `template-payload.ts`) para
// avisar ANTES de submeter: uma recusa não é gratuita — consome o rate limit de
// criação da WABA e, se o template chegar a existir, queima o nome por 30 dias.
import { useId, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { PlusIcon, TrashIcon } from '@/components/icons';
import { useCreateWhatsappTemplateMutation } from '../api';
import {
  CATEGORY_LABEL,
  TEMPLATE_LIMITS,
  extractVariableIndexes,
  renderPreview,
  type TemplateButton,
} from '../model';
import styles from './TemplateEditorModal.module.css';

const buttonSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('QUICK_REPLY'),
    text: z.string().trim().min(1, 'Informe o texto.').max(TEMPLATE_LIMITS.buttonTextMax),
  }),
  z.object({
    type: z.literal('URL'),
    text: z.string().trim().min(1, 'Informe o texto.').max(TEMPLATE_LIMITS.buttonTextMax),
    url: z
      .string()
      .trim()
      .min(1, 'Informe o link.')
      .max(TEMPLATE_LIMITS.buttonUrlMax)
      .regex(/^https?:\/\//i, 'O link precisa começar com http:// ou https://'),
    example: z.string().trim().optional(),
  }),
  z.object({
    type: z.literal('PHONE_NUMBER'),
    text: z.string().trim().min(1, 'Informe o texto.').max(TEMPLATE_LIMITS.buttonTextMax),
    phoneNumber: z.string().trim().min(1, 'Informe o telefone.').max(TEMPLATE_LIMITS.buttonPhoneMax),
  }),
]);

const schema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Informe o nome.')
      .max(TEMPLATE_LIMITS.nameMax)
      .regex(/^[a-z0-9_]+$/, 'Use apenas letras minúsculas, números e underscore.'),
    language: z.string().min(2),
    category: z.enum(['UTILITY', 'MARKETING', 'AUTHENTICATION']),
    body: z.string().trim().min(1, 'Escreva o corpo da mensagem.').max(TEMPLATE_LIMITS.bodyMax),
    variables: z.array(z.string()).default([]),
    footer: z.string().trim().max(TEMPLATE_LIMITS.footerMax).optional(),
    buttons: z.array(buttonSchema).max(TEMPLATE_LIMITS.buttonsMax).default([]),
  })
  .superRefine((value, ctx) => {
    const indexes = extractVariableIndexes(value.body);
    const trimmed = value.body.trim();

    // Erro 2388299 da Meta: variável colada na borda é recusa garantida.
    if (/^\{\{\s*\d+\s*\}\}/.test(trimmed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message: 'O corpo não pode começar com uma variável. Escreva algum texto antes.',
      });
    }
    if (/\{\{\s*\d+\s*\}\}$/.test(trimmed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message: 'O corpo não pode terminar com uma variável. Escreva algum texto depois.',
      });
    }

    const ordenado = [...indexes].sort((a, b) => a - b);
    if (ordenado.some((n, i) => n !== i + 1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message: 'Numere as variáveis em sequência a partir de {{1}}.',
      });
    }

    // A Meta exige um exemplo por variável — sem eles a submissão nem sai.
    indexes.forEach((_, i) => {
      if (!value.variables[i]?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variables', i],
          message: 'Preencha um exemplo.',
        });
      }
    });

    if (value.footer && extractVariableIndexes(value.footer).length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['footer'],
        message: 'O rodapé não aceita variáveis.',
      });
    }

    const urls = value.buttons.filter((b) => b.type === 'URL').length;
    if (urls > TEMPLATE_LIMITS.urlButtonsMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['buttons'],
        message: `No máximo ${TEMPLATE_LIMITS.urlButtonsMax} botões de link.`,
      });
    }
    const phones = value.buttons.filter((b) => b.type === 'PHONE_NUMBER').length;
    if (phones > TEMPLATE_LIMITS.phoneButtonsMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['buttons'],
        message: `No máximo ${TEMPLATE_LIMITS.phoneButtonsMax} botão de telefone.`,
      });
    }

    value.buttons.forEach((b, i) => {
      if (b.type !== 'URL') return;
      const vars = extractVariableIndexes(b.url);
      if (vars.length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['buttons', i, 'url'],
          message: 'O link aceita no máximo uma variável.',
        });
      }
      if (vars.length === 1 && !/\{\{\s*\d+\s*\}\}$/.test(b.url.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['buttons', i, 'url'],
          message: 'A variável do link só pode ficar no fim do endereço.',
        });
      }
      if (vars.length === 1 && !b.example?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['buttons', i, 'example'],
          message: 'Informe um exemplo de link completo.',
        });
      }
    });
  });

type FormValues = z.infer<typeof schema>;

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en_US', label: 'Inglês (EUA)' },
  { value: 'es', label: 'Espanhol' },
];

const CATEGORIES = (['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const).map((c) => ({
  value: c,
  label: CATEGORY_LABEL[c],
}));

/** Mensagem de erro do backend, preferindo o motivo real da Meta. */
function backendMessage(err: unknown, fallback: string): string {
  const data = (
    err as {
      data?: {
        details?: string;
        issues?: Array<{ message?: string }>;
        message?: string;
      };
    }
  )?.data;
  return data?.details ?? data?.issues?.[0]?.message ?? data?.message ?? fallback;
}

export interface TemplateEditorModalProps {
  open: boolean;
  onClose: () => void;
}

export function TemplateEditorModal({ open, onClose }: TemplateEditorModalProps) {
  const toast = useToast();
  const [createTemplate, { isLoading }] = useCreateWhatsappTemplateMutation();
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const categoriaId = useId();
  const idiomaId = useId();
  const corpoId = useId();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      name: '',
      language: 'pt_BR',
      category: 'UTILITY',
      body: '',
      variables: [],
      footer: '',
      buttons: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'buttons' });

  // `useWatch` e não `watch`: os campos de exemplo são registrados dinamicamente
  // (`variables.0`, `variables.1`, …) conforme o corpo muda, e `watch` do array pai
  // não re-renderiza quando um filho registrado depois muda — a prévia ficava
  // congelada com os `{{n}}` crus mesmo com os exemplos preenchidos.
  const body = useWatch({ control, name: 'body' });
  const footer = useWatch({ control, name: 'footer' });
  const variables = useWatch({ control, name: 'variables' });
  const buttons = useWatch({ control, name: 'buttons' });

  // Os campos de exemplo acompanham o corpo: escrever {{2}} faz aparecer o segundo.
  const variableIndexes = useMemo(() => extractVariableIndexes(body ?? ''), [body]);
  const preview = useMemo(
    () => renderPreview(body ?? '', variables ?? []),
    [body, variables],
  );

  function fechar() {
    reset();
    setErroGeral(null);
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    setErroGeral(null);
    try {
      await createTemplate({
        name: values.name,
        language: values.language,
        category: values.category,
        body: values.body,
        // Só os exemplos das variáveis que existem no corpo: apagar um {{2}} depois
        // de preencher o exemplo deixaria um valor órfão que a Meta recusa.
        variables: variableIndexes.map((_, i) => values.variables[i] ?? ''),
        ...(values.footer?.trim() ? { footer: values.footer.trim() } : {}),
        buttons: values.buttons as TemplateButton[],
      }).unwrap();
      toast.success({
        title: 'Template enviado para análise',
        description: 'A Meta costuma responder em minutos, mas pode levar até 24h.',
      });
      fechar();
    } catch (err) {
      setErroGeral(backendMessage(err, 'Não foi possível enviar o template.'));
    }
  });

  return (
    <Modal open={open} onClose={fechar} title="Novo template" size="lg">
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.row}>
          <Input
            label="Nome"
            placeholder="pedido_pronto"
            hint="Só minúsculas, números e underscore. Não dá para mudar depois."
            error={errors.name?.message}
            {...register('name')}
          />
          {/* `htmlFor` + `id` explícitos: o Select é um componente próprio, e um label
              que apenas o envolve não conta como associado para leitores de tela. */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor={categoriaId}>
              Categoria
            </label>
            <Select id={categoriaId} options={CATEGORIES} {...register('category')} />
            <span className={styles.hint}>
              Utilidade para avisos de pedido; Marketing para promoções.
            </span>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={idiomaId}>
              Idioma
            </label>
            <Select id={idiomaId} options={LANGUAGES} {...register('language')} />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={corpoId}>
            Corpo da mensagem
          </label>
          <textarea
            id={corpoId}
            className={styles.textarea}
            rows={5}
            maxLength={TEMPLATE_LIMITS.bodyMax}
            placeholder="Olá {{1}}, seu pedido {{2}} está pronto para retirada."
            aria-invalid={errors.body ? true : undefined}
            {...register('body')}
          />
          <span className={styles.counter}>
            {(body ?? '').length}/{TEMPLATE_LIMITS.bodyMax}
          </span>
          {errors.body ? (
            <span className={styles.error}>{errors.body.message}</span>
          ) : (
            <span className={styles.hint}>
              Use {'{{1}}'}, {'{{2}}'}… para os trechos que mudam a cada envio.
            </span>
          )}
        </div>

        {variableIndexes.length > 0 && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Exemplos das variáveis</legend>
            <p className={styles.hint}>
              A Meta exige um exemplo de cada variável para revisar o template. Eles não
              são enviados aos clientes.
            </p>
            <div className={styles.variables}>
              {variableIndexes.map((n, i) => (
                <Input
                  key={n}
                  label={`Exemplo de {{${i + 1}}}`}
                  placeholder={i === 0 ? 'Maria' : '12345'}
                  error={errors.variables?.[i]?.message}
                  {...register(`variables.${i}` as const)}
                />
              ))}
            </div>
          </fieldset>
        )}

        <Input
          label="Rodapé (opcional)"
          placeholder="Casa da Cópia"
          maxLength={TEMPLATE_LIMITS.footerMax}
          hint={`Até ${TEMPLATE_LIMITS.footerMax} caracteres, sem variáveis.`}
          error={errors.footer?.message}
          {...register('footer')}
        />

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Botões (opcional)</legend>
          {errors.buttons?.message && <span className={styles.error}>{errors.buttons.message}</span>}

          {fields.map((field, i) => {
            const tipo = buttons?.[i]?.type;
            return (
              <div key={field.id} className={styles.button}>
                <div className={styles.buttonHead}>
                  <span className={styles.buttonType}>
                    {tipo === 'URL'
                      ? 'Link'
                      : tipo === 'PHONE_NUMBER'
                        ? 'Telefone'
                        : 'Resposta rápida'}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leadingIcon={<TrashIcon size={14} />}
                    onClick={() => remove(i)}
                    aria-label={`Remover botão ${i + 1}`}
                  >
                    Remover
                  </Button>
                </div>
                <input type="hidden" {...register(`buttons.${i}.type` as const)} />
                <Input
                  label="Texto do botão"
                  maxLength={TEMPLATE_LIMITS.buttonTextMax}
                  error={errors.buttons?.[i]?.text?.message}
                  {...register(`buttons.${i}.text` as const)}
                />
                {tipo === 'URL' && (
                  <>
                    <Input
                      label="Link"
                      placeholder="https://casadacopia.com.br/pedido/{{1}}"
                      hint="A variável, se houver, só pode ficar no fim do endereço."
                      error={
                        (errors.buttons?.[i] as { url?: { message?: string } } | undefined)?.url
                          ?.message
                      }
                      {...register(`buttons.${i}.url` as const)}
                    />
                    <Input
                      label="Exemplo do link"
                      placeholder="https://casadacopia.com.br/pedido/12345"
                      error={
                        (errors.buttons?.[i] as { example?: { message?: string } } | undefined)
                          ?.example?.message
                      }
                      {...register(`buttons.${i}.example` as const)}
                    />
                  </>
                )}
                {tipo === 'PHONE_NUMBER' && (
                  <Input
                    label="Telefone"
                    placeholder="+5511999999999"
                    error={
                      (errors.buttons?.[i] as { phoneNumber?: { message?: string } } | undefined)
                        ?.phoneNumber?.message
                    }
                    {...register(`buttons.${i}.phoneNumber` as const)}
                  />
                )}
              </div>
            );
          })}

          <div className={styles.addButtons}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<PlusIcon size={14} />}
              disabled={fields.length >= TEMPLATE_LIMITS.buttonsMax}
              onClick={() => append({ type: 'QUICK_REPLY', text: '' })}
            >
              Resposta rápida
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<PlusIcon size={14} />}
              disabled={
                fields.length >= TEMPLATE_LIMITS.buttonsMax ||
                (buttons ?? []).filter((b) => b.type === 'URL').length >=
                  TEMPLATE_LIMITS.urlButtonsMax
              }
              onClick={() => append({ type: 'URL', text: '', url: '', example: '' })}
            >
              Link
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<PlusIcon size={14} />}
              disabled={
                fields.length >= TEMPLATE_LIMITS.buttonsMax ||
                (buttons ?? []).filter((b) => b.type === 'PHONE_NUMBER').length >=
                  TEMPLATE_LIMITS.phoneButtonsMax
              }
              onClick={() => append({ type: 'PHONE_NUMBER', text: '', phoneNumber: '' })}
            >
              Telefone
            </Button>
          </div>
        </fieldset>

        <section className={styles.previewWrap} aria-label="Prévia da mensagem">
          <span className={styles.label}>Prévia</span>
          <div className={styles.preview}>
            <p className={styles.previewBody}>{preview || 'A mensagem aparece aqui.'}</p>
            {footer?.trim() && <p className={styles.previewFooter}>{footer}</p>}
            {(buttons ?? []).length > 0 && (
              <div className={styles.previewButtons}>
                {(buttons ?? []).map((b, i) => (
                  <span key={i} className={styles.previewButton}>
                    {b.text || 'Botão'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        {erroGeral && (
          <p className={styles.error} role="alert">
            {erroGeral}
          </p>
        )}

        <footer className={styles.actions}>
          <Button type="button" variant="ghost" onClick={fechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            Enviar para análise
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
